import { useMemo } from "react"
import { DashBoardConditions } from "features/dashBoard/types"
import { CommonGraphStyle, selectedAreaStyle, filter } from "../../common"
import { useTreeMapGraphOption } from "./options"
import { DownLoadData } from "components/download/csv/types"

type useTreeMapGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useTreeMapGraphOption<T>>
}
export const useTreeMapGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
    axisDef, valueDef, formula
  }
}: useTreeMapGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = conditions[axisDef.key]

		const data: {
			code: string
			name: string
			value: number
			count: number
			itemStyle?: CommonGraphStyle
		}[] = []
		for (const record of dataList) {
			const axis = axisDef.val(record)
			if (!axis) continue
			const value = valueDef.val(record)
			if (value === undefined || value === 0) continue
			if (!filter(record, configure, conditions, axisDef.key, targetList)) continue

			const axisList = Array.isArray(axis) ? axis : [axis]
			for (const ax of axisList) {
				if (filterText && ax.name) {
					const axName = ax.name
					if (filterText.split(' ').every((t) => axName.indexOf(t) < 0)) continue
				}
				let it = data.find((r) => r.code === ax.code)
				if (!it) {
					it = {
						code: ax.code,
						name: ax.name ?? '',
						value: value,
						count: 1,
						itemStyle: selectedAreaStyle(condition, ax)
					}
					data.push(it)
				} else {
					if (formula === 'max') {
						if (it.value < value) it.value = value
					} else if (formula === 'min') {
						if (it.value > value) it.value = value
					} else {
						it.value = it.value + value
					}
					it.count = it.count + 1
				}
			}
		}
		if (formula === 'ave') {
			data.forEach((r) => r.value = r.value / r.count)
		}
		return {
			label: condition?.label,
			data,
			csvBuilder: (): DownLoadData | undefined => {
				if (!axisDef?.key || !valueDef?.key) return undefined

				const axisKey = axisDef.key
				const valueKey = valueDef.key
				return {
					header: [{
						title: axisDef.title,
						key: axisDef.key
					}, {
						title: valueDef.title,
						key: valueDef.key,
						unit: valueDef.unit
					}],
					body: data.map((data) => ({
						[axisKey]: data.name,
						[valueKey]: data.value
					}))
				}
			}
		 }
	},
	[dataList, axisDef, valueDef, formula, filterText, conditions, configure, targetList]
)
